import { useState, useEffect, useCallback, Fragment } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { RefreshCw, Trash2, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, Info as InfoIcon, ShieldCheck, Search } from 'lucide-react';
import { logsAPI } from '../services/api';
import { Card, Button, useConfirm, Tooltip } from './ui';
import { selectStyles } from '../utils/adminShared';

const LEVEL_META = {
    error:   { label: 'Error',   color: 'danger',       icon: AlertTriangle },
    warning: { label: 'Warning', color: 'warning',      icon: AlertCircle },
    info:    { label: 'Info',    color: 'primary',      icon: InfoIcon },
    audit:   { label: 'Auditoría', color: 'success',    icon: ShieldCheck }
};

const LEVEL_OPTIONS = [
    { value: '', label: 'Todos los niveles' },
    ...Object.entries(LEVEL_META).map(([v, m]) => ({ value: v, label: m.label }))
];

/**
 * Formatea timestamp UTC a hora local de Perú (America/Lima).
 */
const formatFechaPeru = (isoString) => {
    if (!isoString) return '—';
    try {
        return new Date(isoString).toLocaleString('es-PE', {
            timeZone: 'America/Lima',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    } catch {
        return isoString;
    }
};

const LogsPanel = () => {
    const confirm = useConfirm();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({ error: 0, warning: 0, info: 0, audit: 0 });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(new Set());
    const [selected, setSelected] = useState(new Set());
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });

    // Filtros
    const [filtroLevel, setFiltroLevel] = useState('');
    const [filtroCategory, setFiltroCategory] = useState('');
    const [filtroSearch, setFiltroSearch] = useState('');
    const [filtroFrom, setFiltroFrom] = useState('');
    const [filtroTo, setFiltroTo] = useState('');

    const cargar = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const [logsRes, statsRes] = await Promise.all([
                logsAPI.list({
                    page,
                    pageSize: pagination.pageSize,
                    level: filtroLevel,
                    category: filtroCategory,
                    search: filtroSearch,
                    from: filtroFrom,
                    to: filtroTo
                }),
                logsAPI.stats()
            ]);
            setLogs(logsRes.data || []);
            setPagination(logsRes.pagination);
            setCategories(logsRes.categories || []);
            setStats(statsRes.data || {});
            setSelected(new Set());
            setExpanded(new Set());
        } catch (err) {
            toast.error('Error cargando logs: ' + err.message);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.pageSize, filtroLevel, filtroCategory, filtroSearch, filtroFrom, filtroTo]);

    useEffect(() => {
        cargar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroLevel, filtroCategory, filtroFrom, filtroTo]);

    const toggleExpand = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelected(prev => {
            if (prev.size === logs.length) return new Set();
            return new Set(logs.map(l => l.id));
        });
    };

    const handleDeleteSelected = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        const ok = await confirm({
            title: `¿Eliminar ${ids.length} log${ids.length === 1 ? '' : 's'}?`,
            message: 'Esta acción no se puede deshacer.',
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            const res = await logsAPI.bulkDelete({ ids });
            toast.success(res.message);
            cargar(pagination.page);
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const handleDeleteByLevel = async (level) => {
        const ok = await confirm({
            title: `¿Eliminar todos los logs de "${LEVEL_META[level].label}"?`,
            message: 'Esta acción no se puede deshacer.',
            confirmText: 'Eliminar todos',
            danger: true
        });
        if (!ok) return;
        try {
            const res = await logsAPI.bulkDelete({ level });
            toast.success(res.message);
            cargar(1);
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const handleDeleteBefore = async () => {
        if (!filtroTo) {
            toast.error('Selecciona una fecha máxima (filtro "Hasta") para usar esta acción');
            return;
        }
        const ok = await confirm({
            title: '¿Eliminar logs anteriores?',
            message: `Vas a eliminar todos los logs anteriores a ${filtroTo}. No se puede deshacer.`,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            const res = await logsAPI.bulkDelete({ before: filtroTo });
            toast.success(res.message);
            cargar(1);
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const categoryOptions = [
        { value: '', label: 'Todas las categorías' },
        ...categories.map(c => ({ value: c, label: c }))
    ];

    return (
        <div className="logs-panel">
            {/* Contadores top: ultimos 7 dias por level */}
            <Card className="logs-stats-card">
                <Card.Body>
                    <div className="logs-stats-header">
                        <span className="logs-stats-title">Últimos 7 días</span>
                        <Button variant="ghost" size="sm" leftIcon={<RefreshCw size={13} />} onClick={() => cargar(pagination.page)}>
                            Recargar
                        </Button>
                    </div>
                    <div className="logs-stats-grid">
                        {Object.entries(LEVEL_META).map(([level, meta]) => {
                            const Icon = meta.icon;
                            return (
                                <button
                                    key={level}
                                    type="button"
                                    className={`logs-stat logs-stat--${meta.color} ${filtroLevel === level ? 'is-active' : ''}`}
                                    onClick={() => setFiltroLevel(prev => prev === level ? '' : level)}
                                >
                                    <Icon size={18} />
                                    <div className="logs-stat-body">
                                        <span className="logs-stat-value">{stats[level] || 0}</span>
                                        <span className="logs-stat-label">{meta.label}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            {/* Filtros + acciones */}
            <Card className="logs-filters-card">
                <Card.Body>
                    <div className="logs-filters">
                        <div className="logs-filter logs-filter--search">
                            <Search size={14} />
                            <input
                                className="input"
                                type="text"
                                placeholder="Buscar en mensaje..."
                                value={filtroSearch}
                                onChange={(e) => setFiltroSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') cargar(1); }}
                            />
                        </div>
                        <div className="logs-filter">
                            <label className="label">Nivel</label>
                            <Select
                                styles={selectStyles}
                                options={LEVEL_OPTIONS}
                                value={LEVEL_OPTIONS.find(o => o.value === filtroLevel) || LEVEL_OPTIONS[0]}
                                onChange={(opt) => setFiltroLevel(opt.value)}
                                isSearchable={false}
                            />
                        </div>
                        <div className="logs-filter">
                            <label className="label">Categoría</label>
                            <Select
                                styles={selectStyles}
                                options={categoryOptions}
                                value={categoryOptions.find(o => o.value === filtroCategory) || categoryOptions[0]}
                                onChange={(opt) => setFiltroCategory(opt.value)}
                                isSearchable
                            />
                        </div>
                        <div className="logs-filter">
                            <label className="label">Desde</label>
                            <input
                                className="input"
                                type="date"
                                value={filtroFrom}
                                onChange={(e) => setFiltroFrom(e.target.value)}
                            />
                        </div>
                        <div className="logs-filter">
                            <label className="label">Hasta</label>
                            <input
                                className="input"
                                type="date"
                                value={filtroTo}
                                onChange={(e) => setFiltroTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="logs-actions">
                        {selected.size > 0 ? (
                            <Button
                                variant="danger"
                                size="sm"
                                leftIcon={<Trash2 size={13} />}
                                onClick={handleDeleteSelected}
                            >
                                Eliminar {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
                            </Button>
                        ) : (
                            <>
                                {filtroLevel && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leftIcon={<Trash2 size={13} />}
                                        onClick={() => handleDeleteByLevel(filtroLevel)}
                                    >
                                        Eliminar todos: {LEVEL_META[filtroLevel].label}
                                    </Button>
                                )}
                                {filtroTo && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leftIcon={<Trash2 size={13} />}
                                        onClick={handleDeleteBefore}
                                    >
                                        Eliminar anteriores a {filtroTo}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Tabla */}
            <Card className="logs-table-card">
                <Card.Body>
                    {loading ? (
                        <div className="loading">Cargando logs...</div>
                    ) : logs.length === 0 ? (
                        <div className="empty-message" style={{ padding: 40 }}>
                            No hay logs que coincidan con los filtros.
                        </div>
                    ) : (
                        <div className="logs-tablewrap">
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 36 }}>
                                            <input
                                                type="checkbox"
                                                checked={selected.size === logs.length && logs.length > 0}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th style={{ width: 36 }}></th>
                                        <th style={{ width: 100 }}>Nivel</th>
                                        <th style={{ width: 120 }}>Categoría</th>
                                        <th>Mensaje</th>
                                        <th style={{ width: 70 }}>Repetido</th>
                                        <th style={{ width: 130 }}>Usuario</th>
                                        <th style={{ width: 170 }}>Fecha (Lima)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(log => {
                                        const meta = LEVEL_META[log.level] || LEVEL_META.info;
                                        const Icon = meta.icon;
                                        const isExpanded = expanded.has(log.id);
                                        return (
                                            <Fragment key={log.id}>
                                                <tr className={`logs-row logs-row--${meta.color}`}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(log.id)}
                                                            onChange={() => toggleSelect(log.id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn-icon"
                                                            onClick={() => toggleExpand(log.id)}
                                                            aria-label={isExpanded ? 'Contraer' : 'Expandir'}
                                                        >
                                                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span className={`logs-badge logs-badge--${meta.color}`}>
                                                            <Icon size={11} />
                                                            {meta.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="logs-category">{log.category}</span>
                                                    </td>
                                                    <td className="logs-msg">
                                                        {log.mensaje}
                                                    </td>
                                                    <td className="text-center">
                                                        {log.ocurrencias > 1 && (
                                                            <Tooltip content={`Última: ${formatFechaPeru(log.last_seen_at)}`}>
                                                                <span className="logs-count-badge">×{log.ocurrencias}</span>
                                                            </Tooltip>
                                                        )}
                                                    </td>
                                                    <td className="text-muted">{log.usuario_nombre || '—'}</td>
                                                    <td className="text-muted logs-fecha">
                                                        {formatFechaPeru(log.created_at)}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="logs-expand-row">
                                                        <td colSpan={8}>
                                                            <div className="logs-expand">
                                                                {log.endpoint && (
                                                                    <div className="logs-expand-field">
                                                                        <span className="logs-expand-label">Endpoint:</span>
                                                                        <code>{log.endpoint}</code>
                                                                    </div>
                                                                )}
                                                                {log.ip && (
                                                                    <div className="logs-expand-field">
                                                                        <span className="logs-expand-label">IP:</span>
                                                                        <code>{log.ip}</code>
                                                                    </div>
                                                                )}
                                                                {log.contexto && (
                                                                    <div className="logs-expand-field">
                                                                        <span className="logs-expand-label">Contexto:</span>
                                                                        <pre className="logs-json">{JSON.stringify(
                                                                            typeof log.contexto === 'string' ? JSON.parse(log.contexto) : log.contexto,
                                                                            null, 2
                                                                        )}</pre>
                                                                    </div>
                                                                )}
                                                                {log.stack && (
                                                                    <div className="logs-expand-field">
                                                                        <span className="logs-expand-label">Stack trace:</span>
                                                                        <pre className="logs-stack">{log.stack}</pre>
                                                                    </div>
                                                                )}
                                                                {log.ocurrencias > 1 && (
                                                                    <div className="logs-expand-field text-muted" style={{ fontSize: 11 }}>
                                                                        Primer ocurrencia: {formatFechaPeru(log.created_at)} · Última: {formatFechaPeru(log.last_seen_at)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {pagination.totalPages > 1 && (
                        <div className="logs-pagination">
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={pagination.page <= 1}
                                onClick={() => cargar(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <span className="logs-pagination-info">
                                Página {pagination.page} de {pagination.totalPages}
                                {' · '}
                                {pagination.total} logs
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={pagination.page >= pagination.totalPages}
                                onClick={() => cargar(pagination.page + 1)}
                            >
                                Siguiente
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default LogsPanel;
